import * as z from 'zod'
import langPL from './langs/pl.json'
import langEN from './langs/en.json'

enum FormInputType {
  INPUT_TEXT = 'input',
  TEXTAREA = 'textarea',
  EMAIL = 'email',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  UPLOAD = 'upload',
}

type TLangKeys =
  | 'required'
  | 'min'
  | 'max'
  | 'invalid'
  | 'checkbox'
  | 'radio'
  | 'select'
  | 'file'
  | 'file_max_size'
  | 'file_allowed_formats'

type TLangConfig = Record<TLangKeys, string>

interface IZodTypesOptions {
  minLength?: number
  maxLength?: number
}

const getLangConfig = (): TLangConfig => {
  const langString = document.documentElement.lang || 'pl-PL'
  const lang = langString.split('-')[0]

  switch (lang) {
    case 'pl':
      return langPL
    case 'en':
      return langEN
    default:
      return langPL
  }
}

const getErrorMessage = (key: TLangKeys, val?: string[]) => {
  const lang = getLangConfig()

  let message = lang[key]

  if (val) {
    val.forEach((v) => {
      message = message.replace('{val}', v)
    })
  }

  return message
}

const getZodTypes = (options?: IZodTypesOptions) => {
  const { minLength, maxLength } = options || {}

  const INPUT_TEXT = (val1: string): z.ZodString =>
    z
      .string({ error: getErrorMessage('required', [val1]) })
      .trim()
      .min(1, {
        message: getErrorMessage('required', [val1]),
      })
      .min(minLength || 2, {
        error: getErrorMessage('min', [val1, String(minLength || 2)]),
      })

  const TEXTAREA = (val1: string): z.ZodString =>
    z
      .string({ error: getErrorMessage('required', [val1]) })
      .trim()
      .min(1, {
        message: getErrorMessage('required', [val1]),
      })
      .min(minLength || 10, {
        error: getErrorMessage('min', [val1, String(minLength || 10)]),
      })

  const EMAIL = (val1: string): z.ZodEmail =>
    z.email({ error: getErrorMessage('invalid', [val1]) })

  const SELECT = (val1: string): z.ZodString =>
    z
      .string({ error: getErrorMessage('required', [val1]) })
      .min(1, { error: getErrorMessage('select', [val1]) })

  const CHECKBOX = (): z.ZodString =>
    z.string().includes('true', { error: getErrorMessage('checkbox') })

  const RADIO = (val1: string): z.ZodString =>
    z
      .string({ error: getErrorMessage('required', [val1]) })
      .min(1, { error: getErrorMessage('radio', [val1]) })

  const UPLOAD = (val1: string, val2: string): z.ZodFile =>
    z
      .file({ error: getErrorMessage('file') })
      .max(20_000_000, { error: getErrorMessage('file_max_size', [val1]) })
      .refine(
        (file) =>
          ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(
            file?.type,
          ),
        { error: getErrorMessage('file_allowed_formats', [val2]) },
      )

  return { INPUT_TEXT, TEXTAREA, EMAIL, SELECT, CHECKBOX, RADIO, UPLOAD }
}

/* const fallbackSchema: Record<string, z.ZodObject> = {
  default: z.object({
    input: getZodTypes({ minLength: 2 }).INPUT_TEXT('input'),
    textarea: getZodTypes({ minLength: 10 }).TEXTAREA('textarea'),
    email: getZodTypes().EMAIL('email'),
    select: getZodTypes().SELECT('select'),
    checkbox: getZodTypes().CHECKBOX(),
    radio: getZodTypes().RADIO('radio'),
    upload: getZodTypes().UPLOAD('20 MB', 'jpg/jpeg, png, pdf'),
  }),
} */

/* export type TDefault = z.infer<(typeof FormSchemas)['default']> */

export { FormInputType, getZodTypes }
